import type { MessageDescriptor } from '@lingui/core';
import { useMemo, useState, useCallback } from "react";
import { BaseEditableTable } from "./BaseEditableTable";
import type { EditableCellConfig, FilterConfig } from "./types";
import type { Contact } from "@/types";
import { Button } from "@/components/ui/button";
import { ContactRound } from "lucide-react";
import { toast } from "sonner";
import { getContactCSVColumns } from "@/utils/csvExport";
import type { RowSelectionState, PaginationState, ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useNavigate } from "react-router";
import { useLingui } from "@lingui/react";
import { msg, t } from "@lingui/macro";
import { useEnumOptions } from "@/hooks/settings/useEffectiveEnums";
import { useTableConfig } from "@/hooks/settings/useTableConfig";
import { getContactColumns } from "@/utils/columnUtils";
import { openRecordColumn } from "@/utils/columnDefinitions";
import { LoginStatusBadge } from "@/components/employees/LoginStatusBadge";
import { useLabels } from "@/hooks/settings/useLabels";
import { useAuth } from "@clerk/clerk-react";
import { useVCardExport } from "@/hooks/useVCardExport";

// Add a helper alias representing the value type of any Contact field
type ContactFieldValue = Contact[keyof Contact];

interface ContactsTableProps {
  data: Contact[];
  onCellUpdate?: (
    rowId: string,
    field: keyof Contact,
    newValue: ContactFieldValue
  ) => Promise<void>;
  enableSelection?: boolean;
  // Server-side pagination props
  serverSidePagination?: boolean;
  totalRows?: number;
  pagination?: PaginationState;
  onPaginationChange?: (updater: PaginationState | ((old: PaginationState) => PaginationState)) => void;
  isFetching?: boolean;
  /** First page still loading: skeleton rows instead of the empty state. */
  isLoading?: boolean;
  // Server-side filter/sort/search state, owned by the page (URL)
  initialFilters?: ColumnFiltersState;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  searchTerm?: string;
  onSearchChange?: (search: string) => void;
  hasExternalFilters?: boolean;
  /** Every contact matching the current search and filters, for "Export all". */
  exportAll?: () => Promise<Contact[]>;
  emptyState?: React.ReactNode;
  /** A failed load: shown in the table body, so the toolbar (and the filter that caused it) stays usable. */
  errorState?: React.ReactNode;
  // Custom toolbar content
  toolbarRight?: React.ReactNode;
}

export function ContactsTable({
  data,
  onCellUpdate,
  enableSelection = false,
  serverSidePagination = false,
  totalRows,
  pagination,
  onPaginationChange,
  isFetching = false,
  isLoading = false,
  initialFilters,
  onFilterChange,
  sorting,
  onSortingChange,
  searchTerm,
  onSearchChange,
  hasExternalFilters = false,
  exportAll,
  emptyState,
  errorState,
  toolbarRight,
}: ContactsTableProps) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { orgId } = useAuth();

  // Get custom enum options
  const { options: statusOptions } = useEnumOptions("contacts", "status");
  const { options: genderOptions } = useEnumOptions("contacts", "gender");

  // Fetch available labels
  const { data: availableLabels = [] } = useLabels(orgId || "", "all");

  // Get table configuration
  const { data: tableConfig } = useTableConfig("contacts");

  // Define the columns for the contacts table using the exported function
  const columns = useMemo<EditableCellConfig<Contact>[]>(
    () =>
      getContactColumns(
        (template: string | MessageDescriptor): string => {
          // Use placeholder API to translate message IDs
          const translations: Record<string, string> = {
            "Internal ID": i18n._(t`table.contacts.columns.internalId`),
            "First Name": i18n._(t`table.contacts.columns.firstName`),
            "Last Name": i18n._(t`table.contacts.columns.lastName`),
            "Email Main": i18n._(t`table.contacts.columns.emailMain`),
            "Mobile Phone": i18n._(t`table.contacts.columns.mobilePhone`),
            Gender: i18n._(t`table.contacts.columns.gender`),
            "Birth Date": i18n._(t`table.contacts.columns.birthDate`),
            "contacts.fields.customerScore": i18n._(
              t`contacts.fields.customerScore`
            ),
            "Created At": i18n._(t`table.contacts.columns.createdAt`),
            "children.enums.gender.Male": i18n._(t`children.enums.gender.male`),
            "children.enums.gender.Female": i18n._(
              t`children.enums.gender.female`
            ),
            "children.enums.gender.Other": i18n._(
              t`children.enums.gender.other`
            ),
            "children.enums.gender.Unknown": i18n._(
              t`children.enums.gender.unknown`
            ),
            // Status translations
            "Status": i18n._(t({ id: "contacts.table.status", message: "Status" })),
            "Active": i18n._(t({ id: "common.active", message: "Active" })),
            "Inactive": i18n._({ id: "common.inactive", message: "Inactive" }),
            "Upcoming": i18n._(t({ id: "contacts.table.upcoming", message: "Upcoming" })),
          };
          // `getContactColumns` only ever passes plain string ids; the descriptor arm
          // exists to satisfy `TranslationFunction` and delegates to `i18n._`.
          if (typeof template !== 'string') {
            return i18n._(template);
          }
          return translations[template] || template;
        },
        navigate,
        {
          status: statusOptions,
          gender: genderOptions,
        },
        { availableLabels }
      ).concat(
        {
          // Parent app login (issue #1892): derived from the list payload, no per-row status call.
          accessorKey: 'clerkUserId',
          id: 'login',
          header: i18n._(msg({ id: 'contacts.login.tableColumn', message: 'Login' })),
          editable: false,
          sortable: false,
          cell: ({ row }) => <LoginStatusBadge clerkUserId={row.original.clerkUserId} />,
        },
        openRecordColumn<Contact>("/contacts"),
      ),
    [i18n, navigate, statusOptions, genderOptions, availableLabels]
  );

  // Create localized CSV columns based on the imported configuration
  const localizedContactCSVColumns = useMemo(() => {
    const baseColumns = getContactCSVColumns();
    return baseColumns.map((column) => ({
      ...column,
      header: (() => {
        // Map column keys to translations
        switch (column.key) {
          case "contactInternalId":
            return i18n._(t`table.contacts.columns.internalId`);
          case "firstname":
            return i18n._(t`table.contacts.columns.firstName`);
          case "lastname":
            return i18n._(t`table.contacts.columns.lastName`);
          case "emailMain":
            return i18n._(t`table.contacts.columns.emailMain`);
          case "emailSecond":
            return i18n._(t`table.contacts.columns.emailSecondary`);
          case "telMobile":
            return i18n._(t`table.contacts.columns.mobilePhone`);
          case "telHome":
            return i18n._(t`table.contacts.columns.homePhone`);
          case "telWork":
            return i18n._(t`table.contacts.columns.workPhone`);
          case "birthdate":
            return i18n._(t`table.contacts.columns.birthDate`);
          case "gender":
            return i18n._(t`table.contacts.columns.gender`);
          case "status":
            return i18n._(t({ id: "contacts.table.status", message: "Status" }));
          case "customerScore":
            return i18n._(t`table.contacts.columns.customerScore`);
          case "labels":
            return i18n._(t`table.contacts.columns.labels`);
          case "notes":
            return i18n._(t`table.contacts.columns.notes`);
          case "createdAt":
            return i18n._(t`table.contacts.columns.createdAt`);
          case "updatedAt":
            return i18n._(t`table.contacts.columns.updatedAt`);
          default:
            return column.header || column.key;
        }
      })(),
    }));
  }, [i18n]);

  // Define filter configuration for each column (text filtering is handled by global search)
  // Only what the list endpoint filters by; text fields go through the search.
  const filterConfig: FilterConfig<Contact> = useMemo(
    () => ({
      status: "select",
      labels: "select",
    }),
    []
  );

  // CSV export lives in the table toolbar. The vCard export sends ids only, so
  // a selection across pages exports every selected contact.
  const { mutate: exportVCard, isPending: isVCardExporting } = useVCardExport("contacts");

  const renderSelectionActions = useCallback(
    (selectedRowIds: string[], clearSelection: () => void) => (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => exportVCard(selectedRowIds)}
          disabled={isVCardExporting}
          title={i18n._(t`table.contacts.actions.exportVCardHint`)}
          className="flex items-center space-x-2"
        >
          <ContactRound className="h-4 w-4" aria-hidden="true" />
          <span>{i18n._(t`table.contacts.actions.exportVCard`)}</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={clearSelection}>
          {i18n._(t`table.contacts.actions.clearSelection`)}
        </Button>
      </>
    ),
    // i18n.locale re-renders the translated labels on a language switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exportVCard, isVCardExporting, i18n, i18n.locale]
  );

  // Default cell update handler if none provided
  const defaultCellUpdateHandler = async (
    rowId: string,
    field: keyof Contact,
    _: ContactFieldValue
  ): Promise<void> => {
    // This is a stub that would be replaced with actual API call
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    // For production, replace with actual API call
    // Development/demo error simulation can be controlled via environment variable
    if (import.meta.env.DEV && Math.random() > 0.9) {
      throw new Error("Simulated API error");
    }

    toast.success(
      i18n
        ._(t`table.contacts.actions.fieldUpdated`)
        .replace("{field}", String(field))
        .replace("{id}", rowId)
    );
  };

  // Prepare column visibility and order from table config
  const defaultColumnVisibility = useMemo(() => {
    if (!tableConfig?.visibleColumns) return undefined;

    // Create visibility state object from visible_columns array
    const visibility: Record<string, boolean> = {};

    // Check if visibleColumns is a non-empty array
    const visibleColumns = tableConfig?.visibleColumns;
    if (visibleColumns && visibleColumns.length > 0) {
      // Apply configured visibility
      columns.forEach((col) => {
        const key = col.id || (col.accessorKey as string);
        visibility[key] = visibleColumns.includes(col.accessorKey as string);
      });
    } else {
      // Default: all columns visible
      columns.forEach((col) => {
        visibility[col.id || (col.accessorKey as string)] = true;
      });
    }
    visibility.open = true;

    return visibility;
  }, [tableConfig?.visibleColumns, columns]);

  const defaultColumnOrder = useMemo(() => {
    return tableConfig?.columnOrder || undefined;
  }, [tableConfig?.columnOrder]);

  return (
    <BaseEditableTable<Contact>
      data={data}
      columns={columns}
      onCellUpdate={onCellUpdate || defaultCellUpdateHandler}
      defaultSortField={
        (tableConfig?.defaultSortColumn as keyof Contact) || "lastname"
      }
      defaultSortOrder={tableConfig?.defaultSortDesc ? "desc" : "asc"}
      enableSelection={enableSelection}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      renderSelectionActions={renderSelectionActions}
      enableSearch={true}
      searchPlaceholder={i18n._(t`table.contacts.search.placeholder`)}
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      enableFilters={true}
      filterConfig={filterConfig}
      onFilterChange={onFilterChange}
      hasExternalFilters={hasExternalFilters}
      sorting={sorting}
      onSortingChange={onSortingChange}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="contacts-table"
      defaultColumnVisibility={defaultColumnVisibility}
      defaultColumnOrder={defaultColumnOrder}
      defaultColumnFilters={initialFilters}
      // Server-side pagination props
      serverSidePagination={serverSidePagination}
      totalRows={totalRows}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      isFetching={isFetching}
      isLoading={isLoading}
      enableExport={true}
      exportAll={exportAll}
      exportColumns={localizedContactCSVColumns}
      exportFileName="contacts"
      emptyState={errorState ?? emptyState}
      filteredEmptyState={errorState}
      toolbarRight={toolbarRight}
    />
  );
}
